import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, RingChart } from '../../tcn/ui-kit';
import { TCN } from '../../tcn/ui-kit/tokens';
import { isNum } from '../shared/format';

/**
 * Module 1 — New Creator Health (3 KPIs), one card per metric with a ring chart
 * (mockup A1.5 — module1-performance.jsx). Big number + "target ≥X%" on the left,
 * RingChart on the right. Below-target = amber, on-target = green. Section
 * title/subtitle is rendered by the page (SectionHeader).
 *   - t14_ratio (target 40%)
 *   - go_live_rate_15d_45h_first30 (target 24%)
 *   - first_30d_pk_count_20_ratio (target 13%)
 */
const NewCreatorHealthCard = ({ data, thresholds = {} }) => {
  const { t } = useTranslation();
  if (!data) return null;

  const items = [
    {
      key: 't14_ratio',
      label: t('tcn.m1.newCreator.t14', '≥5 Valid LIVE Days in first 14d'),
      value: data.t14_ratio,
      target: thresholds.t14_ratio ?? 40,
    },
    {
      key: 'go_live_rate_15d_45h_first30',
      label: t('tcn.m1.newCreator.goLive15d45h', 'Go LIVE Rate 15D/45H in first 30d'),
      value: data.go_live_rate_15d_45h_first30,
      target: thresholds.go_live_rate_15d_45h_first30 ?? 24,
    },
    {
      key: 'first_30d_pk_count_20_ratio',
      label: t('tcn.m1.newCreator.first30PK', 'First 30-day Valid PK ≥20'),
      value: data.first_30d_pk_count_20_ratio,
      target: thresholds.first_30d_pk_count_20_ratio ?? 13,
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {items.map((item) => {
        const known = isNum(item.value);
        const meeting = known && item.value >= item.target;
        const colorHex = !known ? TCN.textDim : meeting ? TCN.green : TCN.amber;
        return (
          <Card key={item.key}>
            <div style={{ fontSize: 13, fontWeight: 600, color: TCN.text }}>{item.label}</div>
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-end',
                justifyContent: 'space-between',
                marginTop: 12,
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: 28,
                    fontWeight: 700,
                    letterSpacing: '-0.8px',
                    color: colorHex,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {known ? Math.round(item.value) : '—'}
                  <span style={{ fontSize: 14, color: TCN.textDim }}>%</span>
                </div>
                <div style={{ fontSize: 11, color: TCN.textMute, marginTop: 2 }}>
                  {t('tcn.m1.newCreator.targetGte', 'target ≥{{target}}%', { target: item.target })}
                </div>
              </div>
              <RingChart
                value={known ? item.value : 0}
                target={item.target}
                size={64}
                stroke={6}
                color={colorHex}
                label={known ? `${Math.round(item.value)}%` : '—'}
              />
            </div>
          </Card>
        );
      })}
    </div>
  );
};

export default NewCreatorHealthCard;
